(function ($) {
  "use strict";

  const COORDINATE_DECIMALS = 7;
  const STREET_ZOOM = 17;
  const AREA_ZOOM = 14;
  const MIN_MOVE_METERS = 5;
  const MAP_PAN_KEYS = ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Home", "End", "PageUp", "PageDown"];
  const GEOCODE_DELAY_MS = 500;
  const OPTION_WAIT_TIMEOUT_MS = 8000;
  const OPTION_WAIT_INTERVAL_MS = 100;
  const ADDRESS_MAX_LENGTH = 120;
  const GEOCODED_ADDRESS_MAX_LENGTH = 500;
  const NOTE_MAX_LENGTH = 500;
  const EARTH_RADIUS_METERS = 6371000;
  const PREFERRED_GEOCODE_TYPES = ["street_address", "premise", "subpremise", "establishment", "point_of_interest", "route", "intersection"];
  const SKIPPED_GEOCODE_TYPES = ["plus_code", "country", "administrative_area_level_1", "postal_code"];
  const COUNTRY_SUFFIX = /(?:^|[,\s]+)(?:Cambodia|កម្ពុជា)\s*$/;
  const CLUSTER_CONTINUATION = /[\u17B4-\u17D3\u17DD\uDC00-\uDFFF]/;
  const KHMER_COENG = "\u17D2";
  const COMMA_CUT_MIN_RATIO = 0.6;

  function notify(message, type = "error") {
    if (!message) {
      return;
    }

    if (typeof window.showToast === "function") {
      window.showToast(message, type);

      return;
    }

    window.alert(message);
  }

  function csrfToken() {
    return $('meta[name="csrf-token"]').attr("content");
  }

  function firstErrorMessage(xhr) {
    const json = xhr && xhr.responseJSON;

    if (json && json.errors) {
      const first = Object.values(json.errors)[0];

      return Array.isArray(first) ? first[0] : first;
    }

    return json && json.message ? json.message : null;
  }

  function installGoogleMapsLoader(options) {
    if (window.google && window.google.maps && window.google.maps.importLibrary) {
      return;
    }

    (g=>{var h,a,k,p="The Google Maps JavaScript API",c="google",l="importLibrary",q="__ib__",m=document,b=window;b=b[c]||(b[c]={});var d=b.maps||(b.maps={}),r=new Set,e=new URLSearchParams,u=()=>h||(h=new Promise(async(f,n)=>{await (a=m.createElement("script"));e.set("libraries",[...r]+"");for(k in g)e.set(k.replace(/[A-Z]/g,t=>"_"+t[0].toLowerCase()),g[k]);e.set("callback",c+".maps."+q);a.src=`https://maps.${c}apis.com/maps/api/js?`+e;d[q]=f;a.onerror=()=>h=n(Error(p+" could not load."));a.nonce=m.querySelector("script[nonce]")?.nonce||"";m.head.append(a)}));d[l]?console.warn(p+" only loads once. Ignoring:",g):d[l]=(f,...n)=>r.add(f)&&u().then(()=>d[l](f,...n))})(options);
  }

  let mapLibraries = null;
  let isAuthFailureWatched = false;

  function markMapsAuthFailure() {
    if (isAuthFailureWatched) {
      return;
    }

    isAuthFailureWatched = true;

    const previousHandler = window.gm_authFailure;

    window.gm_authFailure = function () {
      document.querySelectorAll("[data-map-picker]").forEach((element) => element.classList.add("is-failed"));

      if (typeof previousHandler === "function") {
        previousHandler();
      }
    };
  }

  function loadMapLibraries(dataset) {
    if (!mapLibraries) {
      markMapsAuthFailure();

      const options = { key: dataset.apiKey, region: "KH" };

      if (dataset.version) {
        options.v = dataset.version;
      }

      if (dataset.language) {
        options.language = dataset.language;
      }

      installGoogleMapsLoader(options);

      mapLibraries = Promise.all([google.maps.importLibrary("maps"), google.maps.importLibrary("geocoding")]).then(
        ([maps, geocoding]) => ({ Map: maps.Map, Geocoder: geocoding.Geocoder }),
      );

      mapLibraries.catch(() => {
        mapLibraries = null;
      });
    }

    return mapLibraries;
  }

  function roundPoint(point) {
    return {
      lat: Number(Number(point.lat).toFixed(COORDINATE_DECIMALS)),
      lng: Number(Number(point.lng).toFixed(COORDINATE_DECIMALS)),
    };
  }

  function pointKey(point) {
    return Number(point.lat).toFixed(5) + "," + Number(point.lng).toFixed(5);
  }

  function readPoint(latitude, longitude) {
    const lat = parseFloat(latitude);
    const lng = parseFloat(longitude);

    if (!Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) {
      return null;
    }

    return { lat, lng };
  }

  function metersBetween(from, to) {
    const toRadians = (degrees) => (degrees * Math.PI) / 180;
    const dLat = toRadians(to.lat - from.lat);
    const dLng = toRadians(to.lng - from.lng);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRadians(from.lat)) * Math.cos(toRadians(to.lat)) * Math.sin(dLng / 2) ** 2;

    return 2 * EARTH_RADIUS_METERS * Math.asin(Math.sqrt(a));
  }

  const resolvedAreas = new Map();

  function resolveArea(url, point) {
    const key = pointKey(point);

    if (!resolvedAreas.has(key)) {
      const request = $.ajax({
        url,
        type: "GET",
        dataType: "json",
        data: { latitude: point.lat.toFixed(COORDINATE_DECIMALS), longitude: point.lng.toFixed(COORDINATE_DECIMALS) },
      }).then((response) => (response && response.data && response.data.state ? response.data : null));

      resolvedAreas.set(key, request);
      request.fail(() => resolvedAreas.delete(key));
    }

    return resolvedAreas.get(key);
  }

  function hasGeocodeType(result, types) {
    return (result.types || []).some((type) => types.includes(type));
  }

  function pickGeocodeResult(results) {
    const candidates = (Array.isArray(results) ? results : []).filter((result) => result && result.formatted_address);

    return (
      candidates.find((result) => hasGeocodeType(result, PREFERRED_GEOCODE_TYPES)) ||
      candidates.find((result) => !hasGeocodeType(result, SKIPPED_GEOCODE_TYPES)) ||
      candidates.find((result) => hasGeocodeType(result, ["plus_code"])) ||
      null
    );
  }

  function startsCluster(text, index) {
    return !CLUSTER_CONTINUATION.test(text.charAt(index)) && text.charAt(index - 1) !== KHMER_COENG;
  }

  function cutAddressLine(text, maxLength) {
    if (text.length <= maxLength) {
      return text;
    }

    let end = maxLength;

    while (end > 0 && !startsCluster(text, end)) {
      end--;
    }

    const line = text.slice(0, end);
    const lastComma = line.lastIndexOf(",");

    return (lastComma >= maxLength * COMMA_CUT_MIN_RATIO ? line.slice(0, lastComma) : line).trim();
  }

  function toAddressSuggestion(formattedAddress) {
    const text = String(formattedAddress || "")
      .replace(/\s+/g, " ")
      .replace(COUNTRY_SUFFIX, "")
      .trim();

    return cutAddressLine(text, ADDRESS_MAX_LENGTH);
  }

  const geocodedAddresses = [];

  function reverseGeocode(geocoder, point) {
    const nearby = geocodedAddresses.find((entry) => metersBetween(entry.point, point) <= MIN_MOVE_METERS);

    if (nearby) {
      return nearby.request;
    }

    const entry = { point, request: null };

    entry.request = geocoder
      .geocode({ location: point, region: "KH", fulfillOnZeroResults: true })
      .then(({ results }) => {
        const result = pickGeocodeResult(results);

        return result ? result.formatted_address : "";
      })
      .catch(() => {
        geocodedAddresses.splice(geocodedAddresses.indexOf(entry), 1);

        return "";
      });

    geocodedAddresses.push(entry);

    return entry.request;
  }

  function describeArea(area) {
    if (!area) {
      return "";
    }

    return [area.ward, area.city, area.state]
      .filter(Boolean)
      .map((location) => location.name)
      .join(", ");
  }

  function hasOption($select, value) {
    return $select.find("option").filter((_, option) => option.value === value).length > 0;
  }

  function waitForOption($select, value) {
    return new Promise((resolve) => {
      const startedAt = Date.now();

      (function check() {
        if (hasOption($select, value)) {
          resolve(true);

          return;
        }

        if (Date.now() - startedAt > OPTION_WAIT_TIMEOUT_MS) {
          resolve(false);

          return;
        }

        setTimeout(check, OPTION_WAIT_INTERVAL_MS);
      })();
    });
  }

  async function fillLocationSelects($scope, area, isCurrent = () => true) {
    const levels = ["state", "city", "ward"]
      .map((type) => ({
        $field: $scope.find(`[name$="${type}"][data-type="${type}"], select[data-type="${type}"]`).first(),
        value: area && area[type] ? String(area[type].id) : "",
      }))
      .filter((level) => level.$field.length);

    for (let index = 0; index < levels.length; index++) {
      if (!isCurrent()) {
        return false;
      }

      const { $field, value } = levels[index];

      if (!$field.is("select")) {
        $field.val(value);

        continue;
      }

      const next = levels[index + 1];
      const needsReload = next && next.$field.is("select") && next.value !== "" && !hasOption(next.$field, next.value);

      if (String($field.val() || "") === value && !needsReload) {
        continue;
      }

      if (!(await waitForOption($field, value)) || !isCurrent()) {
        return false;
      }

      $field.val(value).trigger("change");
    }

    return true;
  }

  async function fillFormLocation($form, area, isCurrent) {
    $form.data("mapFilling", ($form.data("mapFilling") || 0) + 1);

    try {
      return await fillLocationSelects($form, area, isCurrent);
    } finally {
      $form.data("mapFilling", $form.data("mapFilling") - 1);
    }
  }

  function onManualAreaChange($form, callback) {
    $form.on("change", 'select[data-type="state"], select[data-type="city"]', () => {
      if (!$form.data("mapFilling")) {
        callback();
      }
    });
  }

  function isPickupSelected() {
    const $checked = $("input.shipping_method_input:checked");

    return $checked.length > 0 && $checked.filter((_, element) => $(element).data("is-pickup") == 1).length === $checked.length;
  }

  function savedAddressWrapper(id) {
    return $(".list-available-address .address-item-wrapper").filter((_, element) => String($(element).attr("data-id")) === String(id));
  }

  function itemHasCoordinates($item) {
    return Boolean(readPoint($item.attr("data-latitude"), $item.attr("data-longitude")));
  }

  function itemText($item) {
    return $item.find(".address").first().text().replace(/\s+/g, " ").replace(/\s+,/g, ",").trim();
  }

  class MapPicker {
    constructor(root, callbacks = {}) {
      this.root = root;
      this.dataset = root.dataset;
      this.callbacks = callbacks;
      this.map = null;
      this.geocoder = null;
      this.ready = null;
      this.expectedCenter = null;
      this.lastCenter = null;
      this.$hint = $(root).find("[data-map-picker-hint]");
      this.$locate = $(root).find("[data-map-picker-locate]");

      this.$locate.on("click", () => this.locate());
    }

    defaultView() {
      return {
        center: readPoint(this.dataset.centerLatitude, this.dataset.centerLongitude) || { lat: 11.5564, lng: 104.9282 },
        zoom: parseInt(this.dataset.zoom, 10) || 13,
      };
    }

    ensureMap(initialView) {
      if (!this.ready) {
        this.root.classList.add("is-loading");

        this.ready = loadMapLibraries(this.dataset).then(({ Map, Geocoder }) => {
          const view = (typeof initialView === "function" ? initialView() : initialView) || this.defaultView();
          const options = {
            center: view.center,
            zoom: view.zoom,
            clickableIcons: false,
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: false,
            gestureHandling: this.root.classList.contains("map-picker--inline") ? "cooperative" : "greedy",
          };

          if (this.dataset.mapId) {
            options.mapId = this.dataset.mapId;
          }

          const canvas = this.root.querySelector("[data-map-picker-canvas]");

          this.map = new Map(canvas, options);
          this.geocoder = new Geocoder();
          this.expectedCenter = view.center;

          this.map.addListener("dragstart", () => this.reportUserMove());
          this.map.addListener("idle", () => this.handleMapIdle());

          canvas.addEventListener(
            "keydown",
            (event) => {
              if (MAP_PAN_KEYS.includes(event.key)) {
                this.reportUserMove();
              }
            },
            true,
          );

          return this;
        });

        this.ready
          .catch(() => {
            this.ready = null;
            notify(this.dataset.mapFailedMessage);
          })
          .finally(() => this.root.classList.remove("is-loading"));
      }

      return this.ready;
    }

    handleMapIdle() {
      const point = this.center();
      const reference = this.expectedCenter || this.lastCenter;

      this.expectedCenter = null;
      this.lastCenter = point;

      if (reference && metersBetween(reference, point) > MIN_MOVE_METERS) {
        this.reportUserMove();
      }

      if (this.callbacks.onIdle) {
        this.callbacks.onIdle(point);
      }
    }

    reportUserMove() {
      if (this.callbacks.onUserMove) {
        this.callbacks.onUserMove();
      }
    }

    center() {
      const center = this.map.getCenter();

      return roundPoint({ lat: center.lat(), lng: center.lng() });
    }

    moveTo(center, zoom) {
      if (!this.map || !center) {
        return;
      }

      this.expectedCenter = center;
      this.map.setCenter(center);

      if (zoom) {
        this.map.setZoom(zoom);
      }
    }

    locate() {
      if (!this.map) {
        return;
      }

      if (!navigator.geolocation) {
        notify(this.dataset.geolocationFailedMessage);

        return;
      }

      this.$locate.prop("disabled", true);

      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.$locate.prop("disabled", false);
          this.reportUserMove();
          this.moveTo({ lat: position.coords.latitude, lng: position.coords.longitude }, STREET_ZOOM);
        },
        () => {
          this.$locate.prop("disabled", false);
          notify(this.dataset.geolocationFailedMessage);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
      );
    }

    showHint(text) {
      this.$hint.text(text || "").prop("hidden", !text);
    }

    geocode(point) {
      return this.geocoder ? reverseGeocode(this.geocoder, point) : Promise.resolve("");
    }
  }

  const CheckoutLocationPicker = {
    init() {
      this.$modal = $("[data-bb-checkout-map]");

      if (!this.$modal.length) {
        return;
      }

      this.$form = $("form.checkout-form");
      this.$scope = this.$form.find(".customer-address-payment-form");
      this.isSignedIn = $("#address_id").length > 0;
      this.dataset = this.$modal[0].dataset;
      this.$confirm = this.$modal.find("[data-checkout-map-confirm]");
      this.$pinFields = this.$modal.find("[data-pin-fields]");
      this.$pinAddress = this.$modal.find("[data-pin-address]");
      this.$pinNote = this.$modal.find("[data-pin-note]");
      this.$pinArea = this.$modal.find("[data-pin-area-text]");
      this.$pinChange = this.$modal.find("[data-pin-change]");
      this.$pinManual = this.$modal.find("[data-pin-manual]");
      this.$addressForm = this.$modal.find('[data-checkout-map-panel="form"] form');

      this.picker = new MapPicker(this.$modal.find("[data-map-picker]")[0], {
        onIdle: (point) => this.handleIdle(point),
        onUserMove: () => {
          this.state.userMoved = true;
        },
      });

      this.state = {
        panel: "locations",
        choice: null,
        pendingChoice: null,
        userMoved: false,
        pin: null,
        pinChosen: false,
        area: null,
        resolving: false,
        requestId: 0,
        geocoded: "",
        geocoding: false,
        geocodeToken: 0,
        suggestion: "",
        manual: false,
        formSuggestion: "",
        formRequestId: 0,
        formView: null,
        formSaving: false,
        busy: false,
        guestCardText: "",
      };

      $(document).on("click keydown", "[data-checkout-delivery-card]", (event) => {
        if (event.type === "keydown" && event.key !== "Enter" && event.key !== " ") {
          return;
        }

        event.preventDefault();
        this.open();
      });

      $(document).on("pointerenter focusin", "[data-checkout-delivery-card]", () => this.preloadMap());

      document.addEventListener("checkout:open-location-picker", () => this.open());

      this.$modal.on("shown.bs.modal", () => this.handleShown());

      this.$modal.on("click keydown", "[data-checkout-map-option]", (event) => {
        if ($(event.target).closest("[data-checkout-map-edit]").length) {
          return;
        }

        if (event.type === "keydown" && event.key !== "Enter" && event.key !== " ") {
          return;
        }

        event.preventDefault();
        this.choose(String($(event.currentTarget).attr("data-checkout-map-option")));
      });

      this.$modal.on("click", "[data-checkout-map-edit]", (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.openForm(String($(event.currentTarget).attr("data-id")));
      });

      this.$modal.on("click", "[data-checkout-map-add]", () => this.openForm(null));
      this.$modal.on("click", "[data-checkout-map-back]", () => this.closeForm());
      this.$modal.on("click", "[data-pin-change]", () => this.startManual());
      this.$modal.on("click", "[data-checkout-map-confirm]", () => this.confirm());
      this.$modal.on("change", "[data-pin-manual] select", () => this.updateConfirm());

      this.$pinAddress.on("input", () => {
        this.$pinAddress.removeClass("is-invalid");
        this.updatePinSummary();
      });

      this.$addressForm.on("submit", (event) => {
        event.preventDefault();
        this.submitForm();
      });

      onManualAreaChange(this.$addressForm, () => this.clearFormPin());

      $(document).on("change", "#address_id", () => this.refreshCard());

      this.refreshCard();
    },

    currentSelection() {
      if (!this.isSignedIn) {
        return "pin";
      }

      const value = String($("#address_id").val() || "");

      if (!value || value === "new") {
        return "pin";
      }

      return savedAddressWrapper(value).length ? value : "pin";
    },

    preloadMap() {
      if (!this.picker.map) {
        loadMapLibraries(this.picker.dataset).catch(() => {});
      }
    },

    open() {
      this.preloadMap();

      if (!this.picker.map) {
        this.picker.root.classList.add("is-loading");
      }

      this.renderSaved();
      this.prefillPinFields();
      this.showPanel("locations");
      this.state.pendingChoice = this.currentSelection();
      this.$modal.modal("show");
    },

    handleShown() {
      this.picker
        .ensureMap(() => (this.state.panel === "form" ? this.state.formView : this.viewFor(this.state.pendingChoice || "pin")))
        .then(() => {
          if (this.state.panel !== "form") {
            this.choose(this.state.pendingChoice || "pin");
          }
        })
        .catch(() => {});
    },

    savedItem(id) {
      return savedAddressWrapper(id).find(".address-item").first();
    },

    viewFor(choice) {
      if (choice === "pin") {
        const pin = this.state.pin || this.storedPin();

        return pin ? { center: pin, zoom: STREET_ZOOM } : null;
      }

      const $item = this.savedItem(choice);
      const coordinates = readPoint($item.attr("data-latitude"), $item.attr("data-longitude"));

      if (coordinates) {
        return { center: coordinates, zoom: STREET_ZOOM };
      }

      const centre = readPoint($item.attr("data-centre-lat"), $item.attr("data-centre-lng"));

      return centre ? { center: centre, zoom: AREA_ZOOM } : null;
    },

    pinInput(field) {
      const selector = this.isSignedIn ? "[data-pinned-location]" : "[data-guest-location]";

      return this.$form.find(`${selector}[name="address[${field}]"]`);
    },

    storedPin() {
      if (this.isSignedIn && $("#address_id").val() !== "pin") {
        return null;
      }

      return readPoint(this.pinInput("latitude").val(), this.pinInput("longitude").val());
    },

    prefillPinFields() {
      if (this.state.pin) {
        return;
      }

      if (this.storedPin()) {
        this.$pinAddress.val(this.$scope.find("#address_address").val() || "");
        this.$pinNote.val(this.pinInput("address_note").val() || "");
        this.state.geocoded = this.pinInput("geocoded_address").val() || "";
        this.state.pinChosen = true;
      }
    },

    renderSaved() {
      const $list = this.$modal.find("[data-checkout-map-saved]");

      if (!$list.length) {
        return;
      }

      const savedIcon = this.$modal.find('[data-checkout-map-icon="saved"]').html();
      const editIcon = this.$modal.find('[data-checkout-map-icon="edit"]').html();

      $list.empty();

      $(".list-available-address .address-item-wrapper").each((_, element) => {
        const $wrapper = $(element);
        const id = String($wrapper.attr("data-id"));

        if (id === "pin") {
          return;
        }

        const $item = $wrapper.find(".address-item").first();

        const $row = $('<div class="checkout-map-option" role="button" tabindex="0" aria-pressed="false"></div>')
          .attr("data-checkout-map-option", id)
          .append($('<span class="checkout-map-option__icon"></span>').html(savedIcon))
          .append(
            $('<span class="checkout-map-option__body"></span>').append(
              $('<span class="checkout-map-option__title"></span>').text($item.attr("data-name") || ""),
              $('<span class="checkout-map-option__text"></span>').text(itemText($item)),
            ),
          );

        if ($item.attr("data-edit-url")) {
          $row.append(
            $('<button type="button" class="checkout-map-option__edit"></button>')
              .attr({ "data-checkout-map-edit": "", "data-id": id, "aria-label": this.dataset.editLabel, title: this.dataset.editLabel })
              .html(editIcon),
          );
        }

        $list.append($row);
      });

      this.$modal.find("[data-checkout-map-saved-wrapper]").prop("hidden", $list.children().length === 0);

      const maxAddresses = Number(this.dataset.maxAddresses) || 0;
      const isFull = maxAddresses > 0 && $list.children().length >= maxAddresses;

      this.$modal.find("[data-checkout-map-add]").prop("hidden", isFull);
      this.$modal.find("[data-checkout-map-limit]").prop("hidden", !isFull);
    },

    showPanel(panel) {
      this.state.panel = panel;
      this.$modal.find("[data-checkout-map-panel]").each((_, element) => {
        $(element).prop("hidden", $(element).attr("data-checkout-map-panel") !== panel);
      });
      this.$modal.find("[data-checkout-map-footer]").prop("hidden", panel !== "locations");
    },

    choose(choice) {
      this.state.choice = choice;
      this.state.pendingChoice = choice;
      this.state.userMoved = false;

      this.$modal.find("[data-checkout-map-option]").each((_, element) => {
        const isActive = String($(element).attr("data-checkout-map-option")) === choice;

        $(element).toggleClass("is-active", isActive).attr("aria-pressed", isActive ? "true" : "false");
      });

      this.$pinFields.prop("hidden", choice !== "pin");

      if (!this.picker.map) {
        this.updateConfirm();

        return;
      }

      const view = this.viewFor(choice);

      if (choice === "pin") {
        this.picker.showHint(this.state.pinChosen ? "" : this.$modal.find("[data-map-picker]").attr("data-drag-hint"));

        if (view) {
          this.picker.moveTo(view.center, view.zoom);
        }

        this.handleIdle(this.picker.center());
      } else {
        const $item = this.savedItem(choice);

        this.picker.showHint(itemHasCoordinates($item) ? "" : this.$modal.find("[data-map-picker]").attr("data-drag-hint"));

        if (view) {
          this.picker.moveTo(view.center, view.zoom);
        }
      }

      this.updateConfirm();
    },

    handleIdle(point) {
      if (this.state.panel === "form") {
        this.handleFormIdle(point);

        return;
      }

      if (this.state.choice !== "pin") {
        if (this.state.userMoved) {
          this.picker.showHint("");
        }

        this.updateConfirm();

        return;
      }

      const reference = this.state.pin || this.storedPin();
      const isSamePin = Boolean(reference) && metersBetween(reference, point) <= MIN_MOVE_METERS;

      if (this.state.pin && isSamePin && (this.state.area || this.state.resolving)) {
        if (this.state.userMoved && !this.state.pinChosen) {
          this.state.pinChosen = true;
          this.picker.showHint("");
          this.geocodePin(this.state.pin);
          this.updateConfirm();
        }

        return;
      }

      if (this.state.userMoved) {
        this.state.pinChosen = true;
        this.picker.showHint("");

        if (this.state.manual) {
          this.stopManual();
        }
      }

      if (!isSamePin) {
        this.state.geocoded = "";

        if (this.state.suggestion && this.$pinAddress.val().trim() === this.state.suggestion) {
          this.$pinAddress.val("");
        }
      }

      if (!this.state.pin || !isSamePin) {
        this.state.pin = point;
      }

      this.resolvePin(this.state.pin);

      if (this.state.userMoved) {
        this.geocodePin(this.state.pin);
      }

      this.updateConfirm();
    },

    resolvePin(point) {
      const requestId = ++this.state.requestId;
      const pickerData = this.picker.dataset;

      this.state.resolving = true;
      this.state.area = null;
      this.$pinChange.prop("hidden", true);

      if (this.$pinArea.text().trim() === "" || this.$pinArea.hasClass("text-danger")) {
        this.$pinArea.text(pickerData.findingAreaMessage).removeClass("text-danger");
      }

      this.$pinArea.addClass("is-resolving");

      resolveArea(pickerData.resolveUrl, point).then(
        (area) => {
          if (requestId !== this.state.requestId) {
            return;
          }

          this.state.resolving = false;
          this.state.area = area;
          this.$pinArea
            .text(area ? describeArea(area) : pickerData.areaUnknownMessage)
            .toggleClass("text-danger", !area)
            .removeClass("is-resolving");
          this.$pinChange.prop("hidden", !area);
          this.updatePinSummary();
          this.updateConfirm();
        },
        () => {
          if (requestId !== this.state.requestId) {
            return;
          }

          this.state.resolving = false;
          this.state.area = null;
          this.$pinArea.text(pickerData.areaUnknownMessage).addClass("text-danger").removeClass("is-resolving");
          this.updateConfirm();
        },
      );
    },

    geocodePin(point) {
      clearTimeout(this.geocodeTimer);

      const token = ++this.state.geocodeToken;

      this.state.geocoding = true;
      this.updateConfirm();

      this.geocodeTimer = setTimeout(() => {
        const key = pointKey(point);

        this.picker.geocode(point).then((formattedAddress) => {
          if (token !== this.state.geocodeToken) {
            return;
          }

          this.state.geocoding = false;
          this.updateConfirm();

          if (!this.state.pin || pointKey(this.state.pin) !== key) {
            return;
          }

          const text = String(formattedAddress || "");
          const suggestion = toAddressSuggestion(text);
          const current = this.$pinAddress.val().trim();

          this.state.geocoded = text.slice(0, GEOCODED_ADDRESS_MAX_LENGTH);

          if (current === "" || current === this.state.suggestion) {
            this.$pinAddress.val(suggestion);
          }

          this.state.suggestion = suggestion;
          this.updatePinSummary();
        });
      }, GEOCODE_DELAY_MS);
    },

    updatePinSummary() {
      const text = [this.$pinAddress.val().trim(), describeArea(this.state.area)].filter(Boolean).join(" · ");

      if (text) {
        this.$modal.find("[data-pin-summary]").text(text);
      }
    },

    startManual() {
      this.state.manual = true;
      this.$pinManual.prop("hidden", false);
      this.$pinChange.prop("hidden", true);
      fillLocationSelects(this.$pinManual, this.state.area).then(() => this.updateConfirm());
      this.updateConfirm();
    },

    stopManual() {
      this.state.manual = false;
      this.$pinManual.prop("hidden", true);
    },

    manualArea() {
      const area = {};

      ["state", "city", "ward"].forEach((type) => {
        const $select = this.$pinManual.find(`select[data-type="${type}"]`);
        const value = String($select.val() || "");

        area[type] = value ? { id: value, name: $select.find("option:selected").text().trim() } : null;
      });

      return area;
    },

    updateConfirm() {
      let isEnabled = false;

      if (this.state.choice === "pin") {
        isEnabled =
          Boolean(this.state.pin) &&
          this.state.pinChosen &&
          !this.state.resolving &&
          !(this.state.geocoding && this.$pinAddress.val().trim() === "") &&
          (this.state.manual ? this.isManualAreaComplete() : Boolean(this.state.area));
      } else if (this.state.choice) {
        isEnabled = itemHasCoordinates(this.savedItem(this.state.choice)) || this.state.userMoved;
      }

      this.$confirm.prop("disabled", !isEnabled || this.state.busy);
    },

    isManualAreaComplete() {
      const area = this.manualArea();

      return Boolean(area.state) && (this.isSignedIn || Boolean(area.city));
    },

    async confirm() {
      if (this.$confirm.prop("disabled") || this.state.busy) {
        return;
      }

      this.state.busy = true;
      this.$confirm.addClass("btn-loading");
      this.updateConfirm();

      try {
        const isConfirmed = this.state.choice === "pin" ? await this.confirmPin() : await this.confirmSaved(this.state.choice);

        if (isConfirmed) {
          this.$modal.modal("hide");
          document.dispatchEvent(new CustomEvent("checkout:location-confirmed"));
        }
      } finally {
        this.state.busy = false;
        this.$confirm.removeClass("btn-loading");
        this.updateConfirm();
      }
    },

    async confirmPin() {
      const line = this.$pinAddress.val().trim().slice(0, ADDRESS_MAX_LENGTH);

      if (!line) {
        this.$pinAddress.addClass("is-invalid");
        this.$modal.find("[data-pin-address-error]").text(this.dataset.addressRequiredMessage);
        this.$pinAddress.trigger("focus");

        return false;
      }

      const area = this.state.manual ? this.manualArea() : this.state.area;
      const pin = this.state.pin;
      const values = {
        latitude: pin.lat.toFixed(COORDINATE_DECIMALS),
        longitude: pin.lng.toFixed(COORDINATE_DECIMALS),
        address_note: this.$pinNote.val().trim().slice(0, NOTE_MAX_LENGTH),
        geocoded_address: this.state.geocoded,
        location_manual: this.state.manual ? "1" : "0",
      };
      const fullText = [line, describeArea(area)].filter(Boolean).join(", ");

      this.$form.attr("data-map-filling", "1");

      try {
        this.$scope.find("#address_address").val(line);

        if (this.isSignedIn) {
          this.writePinnedItem(line, area, values, fullText);
          $("#address_id").val("pin").trigger("change");
        } else {
          this.state.guestCardText = fullText;
        }

        Object.entries(values).forEach(([field, value]) => {
          if (!this.isSignedIn && field === "location_manual") {
            return;
          }

          this.pinInput(field).val(value).prop("disabled", false);
        });

        await fillLocationSelects(this.$scope, area);
      } finally {
        this.$form.removeAttr("data-map-filling");
      }

      this.refreshCard();

      return true;
    },

    writePinnedItem(line, area, values, fullText) {
      const $list = $(".list-available-address");
      let $wrapper = savedAddressWrapper("pin");

      if (!$wrapper.length) {
        $wrapper = $('<div class="address-item-wrapper" data-id="pin"></div>');
        $list.prepend($wrapper);
      }

      const $item = $('<div class="address-item"></div>')
        .attr({
          "data-id": "pin",
          "data-name": this.dataset.customerName || "",
          "data-phone": this.dataset.customerPhone || "",
          "data-email": this.dataset.customerEmail || "",
          "data-country": this.$scope.find('[name="address[country]"]').first().val() || "",
          "data-state": area && area.state ? area.state.id : "",
          "data-city": area && area.city ? area.city.id : "",
          "data-ward": area && area.ward ? area.ward.id : "",
          "data-address": line,
          "data-zip_code": "",
          "data-latitude": values.latitude,
          "data-longitude": values.longitude,
        })
        .append($('<p class="name"></p>').text(this.dataset.customerName || ""), $('<p class="address"></p>').text(fullText));

      $wrapper.empty().append($item);

      let $option = $("#address_id option").filter((_, option) => option.value === "pin");

      if (!$option.length) {
        $option = $('<option value="pin"></option>');
        $("#address_id option")
          .filter((_, option) => option.value === "new")
          .after($option);
      }

      $option.text(fullText);
    },

    async confirmSaved(id) {
      const $item = this.savedItem(id);

      if (!$item.length) {
        return false;
      }

      if (this.state.userMoved || !itemHasCoordinates($item)) {
        const point = this.picker.center();

        try {
          const response = await $.ajax({
            url: $item.attr("data-coordinates-url"),
            type: "PUT",
            dataType: "json",
            headers: { "X-CSRF-TOKEN": csrfToken() },
            data: {
              latitude: point.lat.toFixed(COORDINATE_DECIMALS),
              longitude: point.lng.toFixed(COORDINATE_DECIMALS),
            },
          });

          if (response.error) {
            notify(response.message);

            return false;
          }

          savedAddressWrapper(id).html(response.data.html);
        } catch (xhr) {
          notify(firstErrorMessage(xhr));

          return false;
        }
      }

      this.$form.attr("data-map-filling", "1");

      try {
        $("#address_id").val(id).trigger("change");
      } finally {
        this.$form.removeAttr("data-map-filling");
      }

      this.refreshCard();

      return true;
    },

    refreshCard() {
      const $card = $("[data-checkout-delivery-card]");

      if (!$card.length) {
        return;
      }

      const $text = $card.find("[data-delivery-card-text]");
      const $name = $card.find("[data-delivery-card-name]");
      const $warning = $card.find("[data-delivery-card-warning]");

      if (!this.isSignedIn) {
        if (this.state.guestCardText) {
          $text.text(this.state.guestCardText);
          $card.find(".checkout-location-card__error").remove();
        }

        return;
      }

      const value = String($("#address_id").val() || "");
      const $item = value && value !== "new" ? this.savedItem(value) : $();

      if (!$item.length) {
        $name.prop("hidden", true).text("");
        $text.text($text.attr("data-empty-text"));
        $warning.prop("hidden", true);

        return;
      }

      $name.text($item.attr("data-name") || "").prop("hidden", !$item.attr("data-name"));
      $text.text(itemText($item));
      $warning.prop("hidden", value === "pin" || itemHasCoordinates($item));
      $card.find(".checkout-location-card__error").remove();
    },

    openForm(id) {
      const $form = this.$addressForm;

      if (!$form.length) {
        return;
      }

      const $item = id ? this.savedItem(id) : $();

      this.state.editingId = id;
      this.state.formSuggestion = "";
      this.state.userMoved = false;
      this.showPanel("form");

      $form[0].reset();

      const validator = $form.data("validator");

      if (validator) {
        validator.resetForm();
      }

      $form.find(".is-invalid, .is-valid").removeClass("is-invalid is-valid");
      $form.find(".checkout-map-form-error").remove();
      $form.attr("action", id ? $item.attr("data-edit-url") : this.dataset.createUrl);

      const values = id
        ? {
            name: $item.attr("data-name"),
            phone: $item.attr("data-phone"),
            email: $item.attr("data-email"),
            address: $item.attr("data-address"),
            zip_code: $item.attr("data-zip_code"),
            latitude: $item.attr("data-latitude"),
            longitude: $item.attr("data-longitude"),
          }
        : {
            name: this.dataset.customerName,
            phone: this.dataset.customerPhone,
            email: this.dataset.customerEmail,
            address: "",
            zip_code: "",
            latitude: "",
            longitude: "",
          };

      ["name", "email", "address", "zip_code", "latitude", "longitude"].forEach((field) => {
        $form.find(`[name="${field}"]`).val(values[field] || "");
      });

      this.setFormPhone(values.phone || "");

      if (id) {
        $form.find('[name="address_type"]').filter((_, input) => input.value === $item.attr("data-address-type")).prop("checked", true);
        $form.find('[name="is_default"][type="checkbox"]').prop("checked", $item.attr("data-is-default") === "1");
        fillFormLocation($form, {
          state: $item.attr("data-state") ? { id: $item.attr("data-state") } : null,
          city: $item.attr("data-city") ? { id: $item.attr("data-city") } : null,
          ward: $item.attr("data-ward") ? { id: $item.attr("data-ward") } : null,
        });
      }

      const view = id ? this.viewFor(id) : null;

      this.state.formView = view;

      if (view) {
        this.picker.moveTo(view.center, view.zoom);
      }

      this.picker.showHint(readPoint(values.latitude, values.longitude) ? "" : this.$modal.find("[data-map-picker]").attr("data-drag-hint"));
    },

    setFormPhone(phone) {
      const $form = this.$addressForm;
      const display = $form.find(".js-phone-number-mask")[0];
      const $full = $form.find(".js-phone-number-full");
      const instance = display && window.intlTelInputGlobals ? window.intlTelInputGlobals.getInstance(display) : null;

      if (instance && phone.startsWith("+")) {
        instance.setNumber(phone);
      } else if (display) {
        display.value = phone;
      }

      if ($full.length) {
        $full.val(phone);
      }

      if (!display) {
        $form.find('[name="phone"]').val(phone);
      }
    },

    closeForm() {
      this.showPanel("locations");
      this.choose(this.state.pendingChoice || "pin");
    },

    clearFormPin() {
      const $coordinates = this.$addressForm.find('[name="latitude"], [name="longitude"]');

      if (!$coordinates.filter((_, input) => input.value !== "").length) {
        return;
      }

      $coordinates.val("");
      this.state.formRequestId++;
      this.state.userMoved = false;
      this.picker.showHint(this.$modal.find("[data-map-picker]").attr("data-drag-hint"));
    },

    handleFormIdle(point) {
      if (!this.state.userMoved) {
        return;
      }

      const $form = this.$addressForm;
      const requestId = ++this.state.formRequestId;
      const pickerData = this.picker.dataset;

      $form.find('[name="latitude"]').val(point.lat.toFixed(COORDINATE_DECIMALS));
      $form.find('[name="longitude"]').val(point.lng.toFixed(COORDINATE_DECIMALS));
      this.picker.showHint("");

      resolveArea(pickerData.resolveUrl, point).then(
        (area) => {
          if (requestId !== this.state.formRequestId) {
            return;
          }

          if (!area) {
            notify(pickerData.areaUnknownMessage, "warning");

            return;
          }

          fillFormLocation($form, area, () => requestId === this.state.formRequestId);
        },
        () => {},
      );

      clearTimeout(this.formGeocodeTimer);

      this.formGeocodeTimer = setTimeout(() => {
        this.picker.geocode(point).then((formattedAddress) => {
          if (requestId !== this.state.formRequestId) {
            return;
          }

          const suggestion = toAddressSuggestion(formattedAddress);
          const $address = $form.find('[name="address"]');
          const current = String($address.val() || "").trim();

          if (current === "" || current === this.state.formSuggestion) {
            $address.val(suggestion);
          }

          this.state.formSuggestion = suggestion;
        });
      }, GEOCODE_DELAY_MS);
    },

    submitForm() {
      const $form = this.$addressForm;
      const validator = $form.data("validator");

      if (this.state.formSaving || (validator && validator.pendingRequest > 0) || ($form.valid && !$form.valid())) {
        return;
      }

      this.state.formSaving = true;

      const $submit = $form.find('[type="submit"]').prop("disabled", true);

      $.ajax({
        url: $form.attr("action"),
        type: "POST",
        data: new FormData($form[0]),
        processData: false,
        contentType: false,
        dataType: "json",
      })
        .done((response) => {
          if (response.error) {
            notify(response.message);

            return;
          }

          this.upsertSavedAddress(response.data);
          notify(response.message, "success");
          this.showPanel("locations");
          this.renderSaved();
          this.choose(String(response.data.id));
        })
        .fail((xhr) => this.showFormErrors(xhr))
        .always(() => {
          this.state.formSaving = false;
          $submit.prop("disabled", false);
        });
    },

    upsertSavedAddress(data) {
      const id = String(data.id);
      let $wrapper = savedAddressWrapper(id);

      if (!$wrapper.length) {
        $wrapper = $('<div class="address-item-wrapper"></div>').attr("data-id", id);
        $(".list-available-address").append($wrapper);
      }

      $wrapper.html(data.html);

      const text = itemText($wrapper.find(".address-item").first());
      let $option = $("#address_id option").filter((_, option) => option.value === id);

      if (!$option.length) {
        $option = $("<option></option>").val(id);
        $("#address_id").append($option);
      }

      $option.text(text);

      if (String($("#address_id").val()) === id) {
        $(".address-item-selected").html($wrapper.html());
        this.refreshCard();
      }
    },

    showFormErrors(xhr) {
      const $form = this.$addressForm;
      const errors = xhr && xhr.responseJSON && xhr.responseJSON.errors;

      $form.find(".checkout-map-form-error").remove();

      if (errors) {
        Object.entries(errors).forEach(([field, messages]) => {
          const $input = $form.find(`[name="${field}"], [name="${field}_display"]`).not('[type="hidden"]').first();
          const message = Array.isArray(messages) ? messages[0] : messages;

          if ($input.length) {
            $input.addClass("is-invalid");
            $('<div class="invalid-feedback d-block checkout-map-form-error"></div>').text(message).insertAfter($input.closest(".iti").length ? $input.closest(".iti") : $input);
          }
        });
      }

      notify(firstErrorMessage(xhr));
    },
  };

  const PickupPicker = {
    init() {
      this.$card = $("[data-checkout-pickup-card]");

      if (!this.$card.length) {
        return;
      }

      this.dataset = this.$card[0].dataset;
      this.$input = $('input[name="pickup_location_id"]');
      this.$modal = $("#checkout-pickup-modal");
      this.pendingId = null;
      this.hasPicked = false;
      this.listItems = null;
      this.distanceRequestId = 0;
      this.$mapBox = this.$card.find("[data-pickup-card-map]");
      this.$mapImage = this.$card.find("[data-pickup-card-map-image]");

      this.$mapImage.on("error", () => this.$mapBox.prop("hidden", true));

      if (this.$mapImage.attr("src") && this.$mapImage[0].complete && !this.$mapImage[0].naturalWidth) {
        this.$mapBox.prop("hidden", true);
      }

      this.$card.on("click keydown", "[data-pickup-card-select]", (event) => {
        if (event.type === "keydown" && event.key !== "Enter" && event.key !== " ") {
          return;
        }

        event.preventDefault();
        this.open();
      });

      document.addEventListener("checkout:open-pickup-picker", () => this.open());

      this.$modal.on("click keydown", "[data-pickup-option]", (event) => {
        if (event.type === "keydown" && event.key !== "Enter" && event.key !== " ") {
          return;
        }

        event.preventDefault();
        this.pendingId = String($(event.currentTarget).attr("data-pickup-option"));
        this.hasPicked = true;
        this.markPending();
      });

      this.$modal.on("click", "[data-pickup-apply]", () => {
        if (this.pendingId) {
          this.select(this.pendingId);
        }

        this.$modal.modal("hide");
      });
    },

    points() {
      return this.$card
        .find("[data-pickup-point]")
        .map((_, element) => ({
          id: String($(element).attr("data-id")),
          name: $(element).attr("data-name"),
          address: $(element).attr("data-address"),
          openingHours: $(element).attr("data-opening-hours"),
          directionsUrl: $(element).attr("data-directions-url"),
          staticMapUrl: $(element).attr("data-static-map-url"),
        }))
        .get();
    },

    point(id) {
      return this.points().find((item) => item.id === String(id)) || null;
    },

    open() {
      if (!this.$modal.length) {
        return;
      }

      this.pendingId = this.$input.val() ? String(this.$input.val()) : null;
      this.hasPicked = false;
      this.renderList(this.listItems || this.points().map((item) => ({ id: item.id, name: item.name, full_address: item.address })));
      this.$modal.modal("show");
      this.loadDistances();
    },

    position() {
      const fallback = readPoint(this.dataset.defaultLatitude, this.dataset.defaultLongitude);

      return new Promise((resolve) => {
        if (!navigator.geolocation) {
          resolve(fallback);

          return;
        }

        navigator.geolocation.getCurrentPosition(
          (position) => resolve({ lat: position.coords.latitude, lng: position.coords.longitude }),
          () => resolve(fallback),
          { timeout: 8000, maximumAge: 300000 },
        );
      });
    },

    loadDistances() {
      const requestId = ++this.distanceRequestId;

      this.position()
        .then((position) =>
          $.ajax({
            url: this.dataset.listUrl,
            type: "GET",
            dataType: "json",
            data: position ? { latitude: position.lat.toFixed(COORDINATE_DECIMALS), longitude: position.lng.toFixed(COORDINATE_DECIMALS) } : {},
          }),
        )
        .then((response) => {
          if (requestId !== this.distanceRequestId || !response || !Array.isArray(response.data)) {
            return;
          }

          this.listItems = response.data;
          this.renderList(this.hasPicked ? this.inListedOrder(response.data) : response.data);
        })
        .catch(() => {});
    },

    inListedOrder(items) {
      const order = this.$modal
        .find("[data-pickup-option]")
        .map((_, element) => String($(element).attr("data-pickup-option")))
        .get();
      const rank = (item) => {
        const index = order.indexOf(String(item.id));

        return index === -1 ? order.length : index;
      };

      return [...items].sort((first, second) => rank(first) - rank(second));
    },

    renderList(items) {
      const $list = this.$modal.find("[data-pickup-list]");
      const distanceTemplate = this.$modal.attr("data-distance-template") || ":distance km";
      const hadFocus = $list[0] ? $list[0].contains(document.activeElement) : false;

      $list.empty();

      items.forEach((item) => {
        const point = this.point(item.id);
        const $meta = $('<span class="checkout-pickup-option__meta"></span>');
        const $body = $('<span class="checkout-pickup-option__body"></span>').append(
          $('<span class="checkout-pickup-option__name"></span>').text(item.name || ""),
          $('<span class="checkout-pickup-option__address"></span>').text(item.full_address || ""),
        );

        if (point && point.openingHours) {
          $body.append($('<span class="checkout-pickup-option__hours"></span>').text(point.openingHours));
        }

        if (item.is_recommended) {
          $meta.append($('<span class="checkout-pickup-option__recommended"></span>').text(this.$modal.attr("data-recommended-text")));
        }

        if (item.distance_km !== null && item.distance_km !== undefined) {
          $meta.append($('<span class="checkout-pickup-option__distance"></span>').text(distanceTemplate.replace(":distance", Number(item.distance_km).toFixed(1))));
        }

        $list.append(
          $('<div class="checkout-pickup-option" role="button" tabindex="0" aria-pressed="false"></div>')
            .attr("data-pickup-option", item.id)
            .append($('<span class="checkout-pickup-option__radio" aria-hidden="true"></span>'), $body, $meta),
        );
      });

      this.markPending();

      if (hadFocus) {
        $list.find("[data-pickup-option].is-active").first().trigger("focus");
      }
    },

    markPending() {
      this.$modal.find("[data-pickup-option]").each((_, element) => {
        const isActive = String($(element).attr("data-pickup-option")) === this.pendingId;

        $(element).toggleClass("is-active", isActive).attr("aria-pressed", isActive ? "true" : "false");
      });

      this.$modal.find("[data-pickup-apply]").prop("disabled", !this.pendingId);
    },

    select(id) {
      const point = this.point(id);

      if (!point) {
        return;
      }

      this.$input.val(point.id);
      this.$card.find("[data-pickup-card-name]").text(point.name);
      this.$card.find("[data-pickup-card-address]").text(point.address || "");
      this.$card.find("[data-pickup-card-hours]").text(point.openingHours || "");
      this.$card.find("[data-pickup-card-hint]").prop("hidden", false);
      this.$card.find("[data-pickup-card-directions]").attr("href", point.directionsUrl);
      this.$card.find("[data-pickup-card-map-wrapper]").prop("hidden", false);
      this.$card.find(".checkout-location-card__error").remove();
      this.showMap(point);
    },

    showMap(point) {
      if (!point.staticMapUrl) {
        this.$mapBox.prop("hidden", true);

        return;
      }

      this.$mapBox.prop("hidden", false);
      this.$mapImage.attr({ src: point.staticMapUrl, alt: point.name || "" });
    },
  };

  const ContactPhone = {
    init() {
      this.$input = $("[data-checkout-contact-phone]");

      if (!this.$input.length) {
        return;
      }

      this.$hidden = $('input[name="contact_phone"]');
      this.isTouched = this.$input.is("[data-touched]");

      this.$input.on("input", () => {
        this.isTouched = this.$input.val().trim() !== "";
        this.syncHidden();
      });

      this.$input.on("countrychange", () => {
        if (this.isRefreshing) {
          return;
        }

        this.isTouched = true;
        this.syncHidden();
      });

      this.$input.on("blur", () => {
        if (!this.isTouched) {
          this.refresh();
        }
      });

      $(document).on("change", "#address_id", () => this.syncHidden());
      document.addEventListener("checkout:pickup-toggled", () => this.syncHidden());

      this.refresh();
      this.syncHidden();
    },

    isUsed() {
      return isPickupSelected() || String($("#address_id").val() || "") === "pin" || this.isSavedAddressWithoutPhone();
    },

    isSavedAddressWithoutPhone() {
      const value = String($("#address_id").val() || "");

      if (isPickupSelected() || !value || value === "new" || value === "pin") {
        return false;
      }

      return !String(savedAddressWrapper(value).find(".address-item").first().attr("data-phone") || "").trim();
    },

    defaultPhone() {
      return this.$input.attr("data-profile-phone") || "";
    },

    phoneWidget() {
      return window.intlTelInputGlobals ? window.intlTelInputGlobals.getInstance(this.$input[0]) : null;
    },

    fullNumber() {
      const value = this.$input.val().trim();
      const widget = this.phoneWidget();

      if (!value || !widget) {
        return value;
      }

      const number = widget.getNumber();

      if (number) {
        return number;
      }

      const country = widget.getSelectedCountryData();

      return country && country.dialCode ? `+${country.dialCode}${value.replace(/\D/g, "")}` : value;
    },

    syncHidden() {
      const isSent = this.isUsed() && (this.isTouched || this.isSavedAddressWithoutPhone());

      this.$hidden.val(isSent ? this.fullNumber() : "");
    },

    refresh() {
      if (this.isTouched) {
        return;
      }

      const phone = this.defaultPhone();
      const widget = this.phoneWidget();

      if (widget && phone.startsWith("+")) {
        this.isRefreshing = true;

        try {
          widget.setNumber(phone);
        } finally {
          this.isRefreshing = false;
        }
      } else {
        this.$input.val(phone);
      }

      this.syncHidden();
    },
  };

  const AddressBookMapPicker = {
    init() {
      $("[data-bb-address-map-picker]").each((_, root) => {
        if (root.dataset.apiKey) {
          this.prepare(root);
        }
      });
    },

    prepare(root) {
      const $form = $(root).closest("form");
      const context = {
        root,
        $form,
        $latitude: $form.find('input[name="latitude"]'),
        $longitude: $form.find('input[name="longitude"]'),
        $address: $form.find('input[name="address"]'),
        $area: $(root).closest(".address-form__map").find("[data-map-picker-area]"),
        $activate: $(root).find("[data-map-picker-activate]"),
        state: { userMoved: false, requestId: 0, suggestion: "" },
        picker: null,
      };
      const preview = context.$activate.find("img[data-map-picker-preview]")[0];

      if (preview) {
        $(preview).on("error", () => this.clearPreview(context));

        if (preview.complete && !preview.naturalWidth) {
          this.clearPreview(context);
        }
      }

      onManualAreaChange($form, () => this.clearPin(context));

      context.$activate.on("pointerenter focusin", () => loadMapLibraries(root.dataset).catch(() => {}));

      context.$activate.on("click", "[data-map-picker-activate-button]", () => {
        context.$activate.remove();
        this.setup(context);
      });
    },

    clearPreview(context) {
      context.$activate.removeClass("has-preview").find("[data-map-picker-preview]").remove();
      context.$activate.find("[data-map-picker-activate-label]").text(context.$activate.attr("data-empty-label"));
    },

    clearPin(context) {
      if (context.$latitude.val() === "" && context.$longitude.val() === "") {
        return;
      }

      context.$latitude.val("");
      context.$longitude.val("");
      context.state.requestId++;
      context.state.userMoved = false;
      context.$area.text("");

      if (context.picker) {
        context.picker.showHint(context.root.dataset.dragHint);
      } else {
        this.clearPreview(context);
      }
    },

    setup(context) {
      const { root, $form, $latitude, $longitude, $address, $area, state } = context;
      let geocodeTimer = null;

      const picker = new MapPicker(root, {
        onUserMove: () => {
          state.userMoved = true;
        },
        onIdle: (point) => {
          if (!state.userMoved) {
            return;
          }

          const requestId = ++state.requestId;

          $latitude.val(point.lat.toFixed(COORDINATE_DECIMALS));
          $longitude.val(point.lng.toFixed(COORDINATE_DECIMALS));
          picker.showHint("");
          $area.text(root.dataset.findingAreaMessage).removeClass("text-danger");

          resolveArea(root.dataset.resolveUrl, point).then(
            (area) => {
              if (requestId !== state.requestId) {
                return;
              }

              $area.text(area ? describeArea(area) : root.dataset.areaUnknownMessage).toggleClass("text-danger", !area);

              if (area) {
                fillFormLocation($form, area, () => requestId === state.requestId);
              }
            },
            () => {
              if (requestId === state.requestId) {
                $area.text(root.dataset.areaUnknownMessage).addClass("text-danger");
              }
            },
          );

          clearTimeout(geocodeTimer);

          geocodeTimer = setTimeout(() => {
            picker.geocode(point).then((formattedAddress) => {
              if (requestId !== state.requestId) {
                return;
              }

              const suggestion = toAddressSuggestion(formattedAddress);
              const current = String($address.val() || "").trim();

              if (current === "" || current === state.suggestion) {
                $address.val(suggestion);
              }

              state.suggestion = suggestion;
            });
          }, GEOCODE_DELAY_MS);
        },
      });

      context.picker = picker;

      const pin = readPoint($latitude.val(), $longitude.val());
      const centre = readPoint(root.dataset.centreLat, root.dataset.centreLng);

      picker.ensureMap(pin ? { center: pin, zoom: STREET_ZOOM } : centre ? { center: centre, zoom: AREA_ZOOM } : null).catch(() => {});

      if (!pin) {
        picker.showHint(root.dataset.dragHint);
      }
    },
  };

  $(() => {
    CheckoutLocationPicker.init();
    PickupPicker.init();
    ContactPhone.init();
    AddressBookMapPicker.init();
  });
})(jQuery);
